import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Box, Container, Paper, Alert, Typography } from "@mui/material";
import { MfaVerificationForm } from "../../components/auth";
import { useAuth } from "../../hooks";

/**
 * Simplified MFA verification page
 *
 * Direct access to URL parameters without state management
 */

const MfaVerificationPage = () => {
  const { isAuthenticated, mfaEmail, requiresMfa, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get email from multiple sources - auth context, URL parameter, or state
  const emailFromUrl = searchParams.get("email");
  const emailFromState = location.state?.email;
  const email = mfaEmail || emailFromUrl || emailFromState || "";
  const from = location.state?.from?.pathname || "/";

  // Handle authentication state changes after MFA verification
  React.useEffect(() => {
    if (isAuthenticated) {
      console.log(
        "MFA verified, checking forcePasswordChange:",
        user?.forcePasswordChange,
      );

      // If user needs to reset password, redirect to forced reset page
      if (user?.forcePasswordChange) {
        navigate("/auth/forced-password-reset", { replace: true });
      } else {
        // Otherwise redirect to intended destination
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate, from]);

  // If no MFA is required and no email, redirect back to login
  if (!requiresMfa && !email) {
    navigate("/auth/login", { replace: true });
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#1e293b", // slate-800 to match sidebar
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Container maxWidth="xs">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
            <Typography component="h2" variant="h5" align="center" gutterBottom>
              MFA Verification
            </Typography>

            {/* Display alert if we don't have an email */}
            {!email && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                Email address is missing. Please try logging in again.
              </Alert>
            )}

            {/* Only show form if we have an email */}
            {email ? (
              <MfaVerificationForm initialEmail={email} />
            ) : (
              <Box sx={{ mt: 3 }}>
                <Typography align="center">
                  Please return to the login page and try again.
                </Typography>
                <Box sx={{ mt: 2, textAlign: "center" }}>
                  <a href="/auth/login">Return to Login</a>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default MfaVerificationPage;
